import { defineStore, type _GettersTree } from 'pinia';
import type { AntennaReadingType } from '@/types/AntennaReadingType';
import type {
    AntennaMonitoringStateType,
    AntennaMonitoringGettersType,
    AntennaMonitoringActionsType,
    AntennaMonitoringStatsType,
} from '@/types/stores/AntennaMonitoringStoreTypes';
import { ConnectionStatusEnum } from '@/types/enums/ConnectionStatusEnum';
import type { AntennaType } from '@/types/AntennaType';
import { handleRequest } from '@/composables';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';
import type { AxiosResponse } from 'axios';
import { useAuthStore } from '@/stores/authStore';

type ExtendedAntennaMonitoringGettersType = AntennaMonitoringGettersType & {
    currentTenantId: (state: AntennaMonitoringStateType) => number | null;
    apiEndpoint: (state: AntennaMonitoringStateType) => string | null;
};

export const useAntennaMonitoringStore = defineStore<
    'antennaMonitoring',
    AntennaMonitoringStateType,
    ExtendedAntennaMonitoringGettersType & _GettersTree<AntennaMonitoringStateType>,
    AntennaMonitoringActionsType
>('antennaMonitoring', {
    state: (): AntennaMonitoringStateType => ({
        readings: [],
        connectionStatus: ConnectionStatusEnum.Disconnected,
        connectionId: null,
        lastUpdateTime: undefined,
        antennas: [],
        isLoading: false,
        selectedTenantId: null,
    }),
    getters: {
        currentTenantId: (state: AntennaMonitoringStateType): number | null => {
            const authStore = useAuthStore();

            if (authStore.user?.is_super_admin) {
                return state.selectedTenantId;
            }

            return authStore.user?.tenant.id || null;
        },

        apiEndpoint: (state: AntennaMonitoringStateType): string | null => {
            const authStore = useAuthStore();

            if (authStore.user?.is_super_admin) {
                return state.selectedTenantId !== null ? `/tenants/${state.selectedTenantId}/antennas` : null;
            }

            return '/tenant/antennas';
        },

        recentReadings: (state: AntennaMonitoringStateType): AntennaReadingType[] => {
            return state.readings.slice(0, 50);
        },

        totalReadings: (state: AntennaMonitoringStateType): number => {
            return state.readings.length;
        },

        totalEpcs: (state: AntennaMonitoringStateType): number => {
            return state.readings.reduce((sum: number, reading: AntennaReadingType) => {
                const epcCount = reading.readings?.length || 0;

                return sum + epcCount;
            }, 0);
        },

        antennaStats: (state): AntennaMonitoringStatsType[] => {
            return state.antennas.map(antenna => {
                const antennaReadings = state.readings.filter(r => r.antennaUuid === antenna.uuid);
                const totalEpcs = antennaReadings.reduce((sum, reading) => {
                    return sum + (reading.readings?.length || 0);
                }, 0);

                const lastReading = antennaReadings[0];

                return {
                    antennaUuid: antenna.uuid,
                    antennaName: antenna.name,
                    readerName: antenna.reader_name,
                    readerId: antenna.reader_id!,
                    totalReadings: antennaReadings.length,
                    totalEpcs,
                    lastReading: lastReading ? lastReading.timestamp : '',
                    currentEpcs: lastReading?.readings?.length || 0,
                };
            });
        },
    },
    actions: {
        initializeForCompanyAdmin(): void {
            const authStore = useAuthStore();
            if (!authStore.user?.is_super_admin && authStore.user?.tenant.id && !this.selectedTenantId) {
                this.selectedTenantId = authStore.user.tenant.id;
            }
        },

        async fetchAntennas(): Promise<void> {
            this.isLoading = true;
            const endpoint = this.apiEndpoint;

            if (endpoint) {
                await handleRequest({
                    method: RequestMethodEnum.Get,
                    endpoint,
                    onSuccess: (response: AxiosResponse<AntennaType[] | { items: AntennaType[] }>) => {
                        const data = response.data;
                        this.antennas = Array.isArray(data) ? data : data.items;
                    },
                });
            }
            this.isLoading = false;
        },

        addReading(reading: AntennaReadingType): void {
            this.readings.unshift(reading);
            this.lastUpdateTime = new Date();

            if (this.readings.length > 1000) {
                this.readings = this.readings.slice(0, 1000);
            }
        },

        updateConnectionStatus(status: ConnectionStatusEnum, id: string | null): void {
            this.connectionStatus = status;
            this.connectionId = id;

            if (status === ConnectionStatusEnum.Connected) {
                this.lastUpdateTime = new Date();
            }
        },

        getAntennaInfo(antennaUuid: string): AntennaType | undefined {
            return this.antennas.find(antenna => antenna.uuid === antennaUuid);
        },

        getAntennaReadings(antennaUuid: string): AntennaReadingType[] {
            return this.readings.filter(reading => reading.antennaUuid === antennaUuid);
        },
    },
});
